import React, { useState, useRef, useEffect } from "react"
import { Check, ChevronDown } from 'lucide-react'

const NestedSelect = ({
  effectiveDate,
  section,
  setEmployerContributions,
  setEmployeeContributions,
  employerContributions,
  employeeContributions,
  earnings
}) => {
  const [initialOptions, setInitialOptions] = useState([
    { id: 1, label: "None" },
    {
      id: 2,
      label: "₹ 1800 Limit",
      items: [
        { id: "overtime", label: "Overtime", checked: false },
        { id: "incentive", label: "Incentive", checked: false },
      ],
    },
    {
      id: 3,
      label: "12% Variable",
      items: [
        { id: "overtime_var", label: "Overtime", checked: false },
        { id: "incentive_var", label: "Incentive", checked: false },
      ],
    },
  ]);
  const [initialOptions2, setInitialOptions2] = useState([
    { id: 1, label: "None" },
    {
      id: 2,
      label: "0.75% Variable",
      items: [
        { id: "overtime_var", label: "Overtime", checked: false },
        { id: "incentive_var", label: "Incentive", checked: false },
      ],
    },
  ]);
  const [initialOptions3, setInitialOptions3] = useState([
    { id: 1, label: "None" },
    {
      id: 2,
      label: "3.25% Variable",
      items: [
        { id: "overtime_var", label: "Overtime", checked: false },
        { id: "incentive_var", label: "Incentive", checked: false },
      ],
    },
  ]);

  const [state, setState] = useState({
    mainOption: initialOptions[0],
    selectedChildren: [],
  });
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const updateOptions = (prev) =>
      prev.map((option) => {
        if (option.label === "None") return option;

        const validLabels = earnings.map((earning) => earning.name);
        const filteredItems = option.items.filter((item) =>
          validLabels.includes(item.label)
        );

        const existingLabels = filteredItems.map((item) => item.label);
        const newItems = earnings
          .filter((earning) => !existingLabels.includes(earning.name))
          .map((earning) => ({
            id: `${earning.name.toLowerCase()}${option.label.includes("Variable") ? "_var" : ""}`,
            label: earning.name,
            checked: false,
          }));

        const combinedItems = [...filteredItems, ...newItems];

        return {
          ...option,
          items: combinedItems,
        };
      });

    setInitialOptions(updateOptions);
    setInitialOptions2(updateOptions);
    setInitialOptions3(updateOptions);

    setState((prevState) => {
      const validIds = earnings.map((earning) =>
        `${earning.name.toLowerCase()}${prevState.mainOption.label.includes("Variable") ? "_var" : ""}`
      );
      const filteredSelectedChildren = prevState.selectedChildren.filter((id) =>
        validIds.includes(id)
      );
      return {
        ...prevState,
        selectedChildren: filteredSelectedChildren,
      };
    });
  }, [earnings]);

  useEffect(() => {
    if (state.selectedChildren.length > 0 && earnings.length > 0) {
      const filterearnings = earnings.filter((earning) => {
        if (state.selectedChildren.includes(`${earning.name.toLowerCase()}${state.mainOption.label.includes("Variable") ? "_var" : ""}`)) {
          return earning
        }
      })

      let calculatedAmount = 0;

      // Calculate based on the selected option
      if (state.mainOption.label === "₹ 1800 Limit") {
        calculatedAmount = filterearnings.reduce((total, earning) =>
          total + (earning.amount * 12) / 100, 0
        );
      } else if (state.mainOption.label.includes("Variable")) {
        const percentage = parseFloat(state.mainOption.label);
        calculatedAmount = filterearnings.reduce((total, earning) =>
          total + (earning.amount * percentage) / 100, 0
        );
      }

      if (section?.type === "employer") {
        setEmployerContributions((prev) => ({
          ...prev,
          [section?.key]: {
            ...prev[section?.key],
            "calculation": state.mainOption.label,
            "amount": calculatedAmount,
            "selectedEarnings": state.selectedChildren
          },
        }));
      } else {
        setEmployeeContributions((prev) => ({
          ...prev,
          [section?.key]: {
            ...prev[section?.key],
            "calculation": state.mainOption.label,
            "amount": calculatedAmount,
            "selectedEarnings": state.selectedChildren,
          },
        }));
      }
    } else {
      // Reset the contribution when "None" is selected or no children are selected
      const contributionUpdate = {
        "calculation": state.mainOption.label,
        "amount": 0,
        "selectedEarnings": [],
      };

      if (section?.type === "employer") {
        setEmployerContributions((prev) => ({
          ...prev,
          [section?.key]: contributionUpdate,
        }));
      } else {
        setEmployeeContributions((prev) => ({
          ...prev,
          [section?.key]: contributionUpdate,
        }));
      }
    }
  }, [state, earnings]);

  const handleMainOptionSelect = (option) => {
    setState({
      mainOption: option,
      selectedChildren: [],
    });
    setIsOpen(false);
  };

  const handleChildOptionSelect = (child) => {
    setState((prevState) => {
      const isSelected = prevState.selectedChildren.includes(child.id);
      return {
        ...prevState,
        selectedChildren: isSelected
          ? prevState.selectedChildren.filter((id) => id !== child.id)
          : [...prevState.selectedChildren, child.id],
      };
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (section?.type === "employer") {
      const currentContribution = employerContributions[section?.key];
      if (currentContribution) {
        const currentOption = section?.key === "pf" ? initialOptions : initialOptions3;
        const matchingOption = currentOption.find(
          (option) => option.label === currentContribution.calculation
        );
        setState({
          mainOption: matchingOption || initialOptions[0],
          selectedChildren: currentContribution.selectedEarnings || [],
        });
      }
    } else {
      const currentOption = section?.key === "pf" ? initialOptions : initialOptions2;
      const currentContribution = employeeContributions[section?.key];
      if (currentContribution) {
        const matchingOption = currentOption.find(
          (option) => option.label === currentContribution.calculation
        );
        setState({
          mainOption: matchingOption || initialOptions2[0],
          selectedChildren: currentContribution.selectedEarnings || [],
        });
      }
    }
  }, [effectiveDate]);

  return (
    <div className="relative w-full md:max-w-[170px]" ref={dropdownRef}>
      <button
        type="button"
        className="relative flex justify-between items-center border-gray-200 bg-white hover:bg-gray-50 border rounded-md w-full text-left transition-colors focus:outline-none salary-detailcs"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="block font-normal text-[#3A3541] text-sm truncate">
          {state.mainOption ? state.mainOption.label : "None"}
        </span>
        <ChevronDown className="w-4 h-4 text-[grey]" />
      </button>
      {isOpen && (
        <ul
          className="z-10 absolute border-gray-200 bg-white shadow-sm mt-1 py-1 border rounded-md w-full max-h-60 text-base sm:text-sm overflow-auto focus:outline-none"
          role="listbox"
        >
          {(section?.type === "employer" ? (section?.key === "pf" ? initialOptions : initialOptions3) : (section?.key === "pf" ? initialOptions : initialOptions2))?.map((option) => (
            <li key={option?.id} className="group">
              <div
                className={`flex justify-between items-center px-3 py-1.5 cursor-pointer ${state?.mainOption?.id === option?.id
                  ? "bg-gray-100 text-[#3A3541]"
                  : "hover:bg-gray-50"
                  }`}
                onClick={() => handleMainOptionSelect(option)}
                role="option"
                aria-selected={state?.mainOption?.id === option?.id}
              >
                <span className="font-normal text-sm">{option?.label}</span>
                {state?.mainOption?.id === option?.id && (
                  <Check className="w-4 h-4 text-[#3A3541]" />
                )}
              </div>
              {option?.items && state.mainOption?.id === option?.id && (
                <ul className="border-gray-100 bg-white border-t">
                  {option?.items?.map((item) => (
                    <li
                      key={item?.id}
                      className={`flex justify-between items-center px-3 py-1.5 pl-6 cursor-pointer ${state?.selectedChildren?.includes(item?.id)
                        ? "bg-gray-100 text-[#3A3541]"
                        : "hover:bg-gray-50"
                        }`}
                      onClick={() => handleChildOptionSelect(item)}
                      role="option"
                      aria-selected={state?.selectedChildren?.includes(item?.id)}
                    >
                      <span className="font-normal text-sm">{item?.label}</span>
                      {state?.selectedChildren?.includes(item?.id) && (
                        <Check className="w-4 h-4 text-[#3A3541]" />
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default NestedSelect;